/**
 * # Input
 *
 * Uses an implementation of `WithHoverAndFocus` that extends functionality of an `input` element
 *
 * ## Props
 *
 * - `as?: React.ElementType`: The HTML element or custom React component to be rendered as the input element. Defaults to `'input'`.
 * - `className?: string`: Additional CSS class names to be applied to the input element.
 * - `onBlur?: (e?: React.FocusEvent<HTMLElement>) => void`: Event handler for the `blur` event.
 * - `onFocus?: () => {}`: Event handler for the `focus` event.
 * - `onMouseEnter?: () => {}`: Event handler for the `mouseenter` event.
 * - `onMouseLeave?: () => {}`: Event handler for the `mouseleave` event.
 * - `error?: boolean`: Indicates whether the input has an error.
 * - `size?: 's' | 'regular' | 'l'`: The size of the input. Defaults to `'regular'`.
 * - `success?: boolean`: Indicates whether the input is in a success state.
 * - `defaultValue?: string`: The default value of the input.
 * - `placeholder?: string`: The placeholder text for the input.
 * - `readOnly?: boolean`: Indicates whether the input is read-only.
 * - `type?: string`: The type of the input element.
 * - `children?: React.ReactNode`: The content to be rendered inside the input element.
 * - `value?: any`: The value of the input element.
 *
 * ## Usage
 *
 * ```tsx
 * import React from 'react';
 * import Input from './Input';
 *
 * const Example = () => {
 *   return (
 *     <Input
 *       as="input"
 *       className="custom-input"
 *       onBlur={() => console.log('Blur')}
 *       onFocus={() => console.log('Focus')}
 *       onMouseEnter={() => console.log('Mouse Enter')}
 *       onMouseLeave={() => console.log('Mouse Leave')}
 *       error={false}
 *       size="regular"
 *       success={true}
 *       defaultValue="Default Value"
 *       placeholder="Enter text"
 *       readOnly={false}
 *       type="text"
 *       value="Input Value"
 *     >
 *       Example Input
 *     </Input>
 *   );
 * };
 *
 * export default Example;
 * ```
 */

import React, {forwardRef, InputHTMLAttributes} from 'react';
import classnames from 'classnames';
import makeDataProps from 'lib/makeDataProps';
import WithHoverAndFocus from '@albert-io/atomic/helpers/WithHoverAndFocus/WithHoverAndFocus.react';
import './input.scss';

export interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  as?: React.ElementType;
  className?: string;
  onBlur?: (e?: React.FocusEvent<HTMLElement>) => void;
  onFocus?: () => {};
  onMouseEnter?: () => {};
  onMouseLeave?: () => {};
  error?: boolean;
  size?: 's' | 'regular' | 'l';
  success?: boolean;
  defaultValue?: string;
  placeholder?: string;
  readOnly?: boolean;
  type?: string;
  children?: React.ReactNode;
  value?: any;
}

export const Input = forwardRef(
  (
    {
      as: WrappedComponent = 'input',
      children,
      className,
      error,
      onBlur,
      onFocus,
      onMouseEnter,
      onMouseLeave,
      size = 'regular',
      success,
      readOnly,
      ...rest
    }: Props,
    ref
  ) => {
    const dataProps = makeDataProps({...rest});
    const passthroughProps = {...rest, ...dataProps};

    return (
      <WithHoverAndFocus
        customOnBlur={onBlur}
        customOnFocus={onFocus}
        customOnMouseEnter={onMouseEnter}
        customOnMouseLeave={onMouseLeave}
      >
        {({hasFocus, hasHover, ...handlers}) => {
          const classNames = classnames('a-input', className, {
            'a-input--error': error,
            'a-input--focus': hasFocus,
            'a-input--hover': hasHover,
            [`a-input--${size}`]: size,
            'a-input--success': success,
            'a-input--read-only': readOnly
          });
          return (
            <WrappedComponent
              ref={ref}
              className={classNames}
              readOnly={readOnly}
              {...passthroughProps}
              {...handlers}
            >
              {children}
            </WrappedComponent>
          );
        }}
      </WithHoverAndFocus>
    );
  }
);

export default Input;
